import { FormattedMessage } from "react-intl";
import { Grid, useMediaQuery } from "@mui/material";
import { Add, Create, Delete } from "@mui/icons-material";

import { ListAction, ListActions, StyledListItem, StyledListWrapper, StyledPagination } from "@framework/styled";
import { IPage } from "@framework/types";
import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { emptyStateString } from "@dzambalaorg/draft-js-utils";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { useDialogDelete, useFetchQuery } from "@dzambalaorg/react-hooks";
import { useGlobalDialogFormCallbacks } from "@framework/shared";
import { Button, List } from "@dzambalaorg/ui-kit";

import { OfficeDialogKeys } from "../../../shared";
import { pageFilter, preparePageInitialValues } from "./schemas";
import { pageFormValidationSchema } from "./form/validation";

const PAGE_URL = "/pages";
const emptyValues = {
  title: "",
  description: emptyStateString,
  slug: "",
};

export const Page = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<IPage>
  >(PAGE_URL, { enableInfiniteScroll: isMd });

  const { openDialog } = useGlobalDialogFormCallbacks<IPage, IPage>();
  const { handleDelete } = useDialogDelete<IPage>();

  const pageCreate = () => {
    openDialog(OfficeDialogKeys.PAGES_CREATE, emptyValues as IPage, {
      refetch,
      initialValues: emptyValues as IPage,
      message: "dialogs.create",
      testId: "PageFormDialog",
      url: `${PAGE_URL}`,
      validationSchema: pageFormValidationSchema,
      filter: pageFilter,
      prepareInitialValues: preparePageInitialValues,
      componentProps: {
        mode: "create",
      },
    });
  };

  const pageEdit = (page: IPage) => () => {
    openDialog(OfficeDialogKeys.PAGES_EDIT, page, {
      refetch,
      initialValues: page,
      message: "dialogs.edit",
      testId: "PageFormDialog",
      url: `${PAGE_URL}/${page.id}`,
      validationSchema: pageFormValidationSchema,
      filter: pageFilter,
      prepareInitialValues: preparePageInitialValues,
      componentProps: {
        mode: "edit",
      },
    });
  };

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "pages"]} />

      <PageHeader message="pages.pages.title">
        <Button intent="secondary" startIcon={<Add />} onClick={pageCreate} data-testid="PageCreateButton">
          <FormattedMessage id="form.buttons.create" />
        </Button>
      </PageHeader>

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(page => (
            <StyledListItem key={page.id}>
              <List.ItemText>{page.title}</List.ItemText>
              <ListActions>
                <ListAction
                  onClick={pageEdit(page)}
                  message="form.buttons.edit"
                  dataTestId="PageEditButton"
                  icon={Create}
                />
                <ListAction
                  onClick={handleDelete(page)}
                  message="form.buttons.delete"
                  dataTestId="PageDeleteButton"
                  icon={Delete}
                />
              </ListActions>
            </StyledListItem>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={PAGE_URL} refetch={refetch} />
    </Grid>
  );
};
